import { useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { isLoggedIn, openSsoLoginPopup, openSsoSignupPopup, extractTokensFromUrl, isJustLoggedOut } from 'common/utils/auth';
import { userStore } from 'store/userStore';
import { Screen, Button } from 'components/ds';
import styles from './Login.module.css';

const POST_LOGIN_RETURN_KEY = 'postLoginReturnUrl';

const sanitizeReturnUrl = (raw: string | null | undefined): string => {
    if (!raw) return '/home';
    return raw.startsWith('/') && !raw.startsWith('//') ? raw : '/home';
};

export const Login = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Open redirect 방지: 상대 경로만 허용
    // 우선순위: query string returnUrl → sessionStorage 백업 → /home
    const rawQueryReturn = searchParams.get('returnUrl');
    const rawStoredReturn = (() => {
        try { return sessionStorage.getItem(POST_LOGIN_RETURN_KEY); } catch { return null; }
    })();
    const returnUrl = sanitizeReturnUrl(rawQueryReturn || rawStoredReturn);

    useEffect(() => {
        // SSO redirect 직전 returnUrl 을 백업 (네이티브 WebView 흐름은 SSO 가 /login 으로
        // 돌아올 때 query string 을 잃어버리므로 sessionStorage 로 보존)
        if (rawQueryReturn) {
            try { sessionStorage.setItem(POST_LOGIN_RETURN_KEY, sanitizeReturnUrl(rawQueryReturn)); } catch {}
        }

        const consumeReturn = () => {
            try { sessionStorage.removeItem(POST_LOGIN_RETURN_KEY); } catch {}
        };

        const goHome = () => {
            userStore.onGetSsoUser();
            consumeReturn();
            navigate(returnUrl, { replace: true });
        };

        // 1. SSO 리다이렉트 콜백: URL에 토큰이 있으면 추출하여 저장
        const tokenData = extractTokensFromUrl();
        if (tokenData) {
            goHome();
            return;
        }

        // 2. 이미 로그인되어 있으면 returnUrl(또는 홈)으로 이동
        if (isLoggedIn()) {
            goHome();
            return;
        }

        /* 2-1. 방금 로그아웃했으면 자동로그인을 건너뜁니다.
         *
         * 앱에서 로그아웃하면 웹은 네이티브에 CLEAR_AUTH 를 보내고 기다리지 않은 채
         * 바로 /login 으로 다시 로드합니다. 그 순간 window.__APP__ 은 네이티브가
         * 직전 값으로 이미 심어둔 상태라 아래 3번이 지운 토큰을 되살리고 홈으로
         * 보냈습니다. 두 번 로그아웃해야 로그인 화면이 나오던 이유입니다.
         * 표시는 읽는 즉시 지워져, 다음부터는 자동로그인이 정상 동작합니다. */
        if (isJustLoggedOut()) return;

        // 3. 네이티브 앱 흐름: window.__APP__.accessToken 이 injected 되어 있는지 확인
        //    (WebViewMain.tsx injectedJavaScriptBeforeContentLoaded 에서 세팅됨)
        const app = (window as any).__APP__;
        if (app?.accessToken) {
            try {
                localStorage.setItem('accessToken', app.accessToken);
                if (app.refreshToken) localStorage.setItem('refreshToken', app.refreshToken);
            } catch {}
            goHome();
            return;
        }

        // 4. 네이티브 SEND_TOKEN postMessage 대기 리스너
        //    첫 렌더 순간엔 accessToken 이 아직 없어도, 곧이어 native → web 브릿지로 도착.
        //    도착 즉시 localStorage 에 저장 + 홈으로 replace 이동해서 로그인 화면에 갇히지 않게 함.
        const isNative = !!((window as any).ReactNativeWebView || app);
        if (!isNative) return;

        const handleTokenMsg = (e: MessageEvent) => {
            try {
                const raw = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
                if (raw?.type !== 'SEND_TOKEN') return;
                const token = raw?.data?.access_token || raw?.data?.accessToken;
                const refresh = raw?.data?.refresh_token || raw?.data?.refreshToken;
                if (!token) return;
                try {
                    localStorage.setItem('accessToken', token);
                    if (refresh) localStorage.setItem('refreshToken', refresh);
                } catch {}
                goHome();
            } catch { /* ignore malformed */ }
        };
        window.addEventListener('message', handleTokenMsg);
        // Android WebView 는 document 에 postMessage 를 dispatch
        document.addEventListener('message', handleTokenMsg as any);
        return () => {
            window.removeEventListener('message', handleTokenMsg);
            document.removeEventListener('message', handleTokenMsg as any);
        };
    }, [navigate, returnUrl, rawQueryReturn]);

    const handleLoginSuccess = useCallback((data?: any) => {
        // SSO에서 받은 데이터에 nickName이 있으면 바로 저장
        if (data?.nickName || data?.nick_name) {
            userStore.onSaveUserInfo({
                userId: data.user_id || data.userId || '',
                userNo: data.user_no || data.userNo || 0,
                nickName: data.nickName || data.nick_name || '',
            });
        }
        userStore.onGetSsoUser();
        try { sessionStorage.removeItem(POST_LOGIN_RETURN_KEY); } catch {}
        navigate(returnUrl, { replace: true });
    }, [navigate, returnUrl]);

    const handleLogin = () => {
        openSsoLoginPopup(handleLoginSuccess);
    };

    const handleSignup = () => {
        openSsoSignupPopup(handleLoginSuccess);
    };

    return (
        <Screen>
            <div className={styles.wrap}>
                {/* 보라 아이콘 대신 워드마크 — 상단바와 같은 표시를 씁니다 */}
                <div className={styles.logo}>
                    <span className={styles.wordmark}>DOKOCREW</span>
                    <h1 className={styles.slogan}>{t('login.slogan')}</h1>
                </div>

                <div className={styles.buttons}>
                    <Button variant="primary" size="lg" onClick={handleLogin}>
                        {t('login.login')}
                    </Button>
                    <Button variant="secondary" size="lg" onClick={handleSignup}>
                        {t('login.signup')}
                    </Button>
                </div>

                <div className={styles.footer}>
                    <span className={styles.footerText}>{t('login.termsAgreement')}</span>
                </div>
            </div>
        </Screen>
    );
};
